import { Suspense, lazy, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

// Lazy load GalaxyBackground to reduce initial bundle size
const GalaxyBackground = lazy(() => import("../animations/GalaxyBackground"));

const HomeHero = () => {
  const heroRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  // Memoize transform ranges for performance
  const transformRanges = useMemo(() => ({
    scale: isMobile ? [1, 1] : [1, 1.5],
    opacity: isMobile ? [1, 1, 1] : [1, 0.3, 0],
    blur: isMobile ? [0, 0] : [0, 10],
  }), [isMobile]);

  // Scroll-based zoom effect - disabled on mobile for performance
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Zoom in effect - disabled on mobile
  const scale = useTransform(scrollYProgress, [0, 1], transformRanges.scale);
  
  // Fade out as zooming in - disabled on mobile
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], transformRanges.opacity);

  // Blur effect for depth - disabled on mobile
  const blur = useTransform(scrollYProgress, [0, 1], transformRanges.blur);
  const filter = useTransform(blur, (value) => `blur(${value}px)`);

  return (
    <motion.section 
      ref={heroRef}
      className="relative w-full overflow-hidden flex items-center justify-center"
      style={{ 
        backgroundColor: 'transparent', 
        height: 'calc(100vh - 88px)',
        marginTop: '88px',
        minHeight: 'calc(100vh - 88px)'
      }}
    >
      {/* Background with zoom effect */}
      <motion.div
        className="absolute inset-0"
        style={{
          scale,
          opacity,
          filter,
        }}
      >
        {/* Three.js Galaxy Background with OrbitControls - Lazy loaded with IntersectionObserver */}
        <Suspense fallback={<div className="absolute inset-0 bg-black" />}>
          <GalaxyBackground variant="orangeWhite" transparent={true} enableZoomAnimation={true} />
        </Suspense>

      </motion.div>

      {/* Overlay for better text readability - Reduced opacity for lighter overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.35) 100%)",
          zIndex: 2,
        }}
      />

      {/* Main Content */}
      <motion.div
        className="relative text-center px-4 max-w-5xl mx-auto pointer-events-none"
        initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 30 }}
        animate={isMobile ? { opacity: 1 } : { opacity: 1, y: 0 }}
        transition={isMobile ? {} : { duration: 1, delay: 0.5 }}
        style={{ zIndex: 30 }}
      >
        {/* Hero Title */}
        <h1
          className="hero-title mb-6 leading-tight"
          style={{
            fontFamily: "'Grift', sans-serif",
            fontSize: "clamp(36px, 7vw, 84px)",
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            color: "#FFFFFF",
            textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
          }}
        >
          <span
            className="inline-block relative"
            style={{ color: "#E67E22" }}
          >
            Endless
            {/* Animated Underline */}
            <motion.div
              className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
              style={{ backgroundColor: "#E67E22" }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            />
          </span>{" "}
          Academy
        </h1>

        {/* Elegant Subtitle */}
        <h2
          className="hero-subtitle font-serif italic mb-8"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(16px, 2vw, 22px)",
            color: "#FFFFFF",
            letterSpacing: "0.08em",
            fontWeight: 500,
            textShadow: "0 2px 10px rgba(0, 0, 0, 1), 0 4px 30px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 0, 0, 0.8)",
            WebkitTextStroke: "0.5px rgba(0, 0, 0, 0.3)",
          }}
        >
          Bachata Online Classes
        </h2>

        {/* CTA Buttons */}
        <div className="hero-cta flex flex-col sm:flex-row gap-3 justify-center items-center pointer-events-auto">
          {/* Primary CTA */}
          <motion.a
            href="/courses"
            className="w-full sm:w-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <button
              className="relative overflow-hidden w-full sm:w-auto inline-flex items-center justify-center gap-2"
              style={{
                padding: "14px 32px",
                fontSize: "16px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: "#FFFFFF",
                background: "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)",
                border: "none",
                borderRadius: "12px",
                boxShadow: "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #F39C12 0%, #E67E22 100%)";
                e.currentTarget.style.boxShadow = "0 8px 32px rgba(230, 126, 34, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span style={{ position: "relative", zIndex: 1 }}>Browse Courses →</span>
            </button>
          </motion.a>

          {/* Secondary CTA */}
          <motion.a
            href="https://klauyros.artoverse.club/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <button
              className="relative overflow-hidden w-full sm:w-auto inline-flex items-center justify-center"
              style={{
                padding: "14px 32px",
                fontSize: "16px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: "#FFFFFF",
                background: "transparent",
                border: "2px solid rgba(255, 255, 255, 0.4)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.95)";
                e.currentTarget.style.color = "#000000";
                e.currentTarget.style.boxShadow = "0 6px 28px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
                e.currentTarget.style.color = "#FFFFFF";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.3)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span style={{ position: "relative", zIndex: 1 }}>Enter the Studio →</span>
            </button>
          </motion.a>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default HomeHero;
